import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye } from 'lucide-react'
import moment from 'moment'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import CommonFilter from '@/components/common/CommonFilter'
import CommonTable, { type TableColumn } from '@/components/common/CommonTable'
import TablePagination from '@/components/common/TablePagination'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
// import type { TableColumn } from '@/interface/common.interface'
import { RoutesLink } from '@/utility/RouteLinks'
import { ThrowError } from '@/api/callApi'
import { API_URL } from '@/api/constant'
import type { IPageInfo, IFilter } from '@/interface/common.interface'
import OffboardingService from '@/services/Offboarding/OffboardingService'
import type { offboarding } from '@/interface/offboarding.interface'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { GetFilterUrl } from '@/utility/helper'

const ExitClearanceList: React.FC = () => {

    const { userId } = useCurrentUser();
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [records, setRecords] = useState<offboarding[]>([])

    const [filter, setFilter] = useState<IFilter>({
        search: "",
        status: "",
    })

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    })

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }))
    }

    const handleSearchChange = (value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev
            return { ...prev, search: value }
        })
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
    }

    const handleFilterChange = (value: string, name: string) => {
        setFilter((prev) => ({ ...prev, [name]: value }))
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
    }

    const handleGetOffboardings = async (
        currentPage = paginationData.currentPage,
        currentLimit = paginationData.pageSize
    ) => {
        setIsLoading(true)
        try {
            const queryParams = {
                page: currentPage,
                limit: currentLimit,
                search: filter.search || undefined,
                // isCompleted: 0,
                handoverToUserId: userId

            }
            const params = GetFilterUrl(queryParams)
            const response = await OffboardingService.getOffboardingList(params)
            if (response?.status) {
                setRecords(response?.data?.offboardings || [])
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }))
            }
        } catch (error) {
            console.error("Error loading offboarding records for clearance:", error)
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        handleGetOffboardings(paginationData.currentPage, paginationData.pageSize)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter])

    const columns: TableColumn<offboarding>[] = [
        {
            key: 'sNo',
            header: 'S.No',
            render: (_item: offboarding, index: number) => (
                <span className="text-muted-foreground font-medium text-sm">
                    {String((paginationData.currentPage - 1) * paginationData.pageSize + index + 1).padStart(2, "0")}
                </span>
            )
        },
        {
            key: 'name',
            header: 'Name',
            render: (item: offboarding) => (
                <div className="flex items-center gap-3">
                    {item?.user?.profilePhoto ? (
                        <img src={`${API_URL}/${item.user.profilePhoto}`} alt="" className="h-10 w-10 rounded-full inline-block object-cover" />
                    ) : (
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm cursor-pointer">
                            {item?.user?.name?.charAt(0)?.toUpperCase()}
                        </div>
                    )}
                    <span
                        className="cursor-pointer font-semibold text-slate-800 hover:text-primary transition-colors underline"
                        onClick={() => navigate(`${RoutesLink.viewExitClearance}/${item.userId}`, { state: { offboardingId: item.id } })}
                    >
                        {item?.user?.name}
                    </span>
                </div>
            )
        },
        {
            key: 'designation',
            header: 'Designation',
            render: (item: offboarding) => (
                <span className="font-medium text-gray-700">{item?.user?.designation?.name || '--'}</span>
            )
        },
        {
            key: 'resignationDate',
            header: 'Resignation Date',
            render: (item: offboarding) => (
                <span className="font-medium text-gray-700 text-sm">
                    {item?.resignationDate ? moment(item.resignationDate).format('DD-MM-YYYY') : '--'}
                </span>
            )
        },
        {
            key: 'lastWorkingDate',
            header: 'Last Working Day',
            render: (item: offboarding) => (
                <span className="font-medium text-gray-700 text-sm">
                    {item?.lastWorkingDate ? moment(item.lastWorkingDate).format('DD-MM-YYYY') : '--'}
                </span>
            )
        },
        // {
        //     key: 'noticePeriod',
        //     header: 'Notice Period',
        //     render: (item: offboarding) => (
        //         <span className="font-medium text-gray-700">{item?.noticePeriod || '--'}</span>
        //     )
        // },
        {
            key: 'handoverTo',
            header: 'Handover To',
            render: (item: offboarding) => (
                <span className="font-medium text-slate-700 font-semibold">{item?.handoverToUser?.name || '--'}</span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: offboarding) => {
                const status = (item?.status || "pending").toLowerCase();
                let statusClasses = "text-amber-600 border-amber-600 bg-amber-50";
                if (status === "completed" || status === "complete" || status === "approved") {
                    statusClasses = "text-green-600 border-green-600 bg-green-50";
                } else if (status === "rejected" || status === "cancelled") {
                    statusClasses = "text-destructive border-destructive bg-destructive/10";
                }
                return (
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold border rounded-full capitalize ${statusClasses}`}>
                        {item?.status || '--'}
                    </span>
                );
            }
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: offboarding) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
                            <span className="sr-only">Open menu</span>
                            <Eye className="h-4 w-4 text-slate-600" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl shadow-lg border-slate-100">
                        <DropdownMenuItem
                            onClick={() => navigate(`${RoutesLink.viewExitClearance}/${item?.userId}`, { state: { offboardingId: item?.id } })}
                            className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50 gap-2 font-medium"
                        >
                            <Eye className="h-4 w-4 text-slate-500" />
                            View Clearance Details
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title='Exit Clearance List'
                    isBackButtonVisible={true}
                />

                <CommonFilter
                    searchValue={filter.search || ""}
                    onSearchChange={handleSearchChange}
                    value={filter}
                    onChange={handleFilterChange}
                    NoAdvanceFilters={true}

                />
            </div>

            <CommonTable
                columns={columns}
                data={records}
                loading={isLoading}
            />

            {records.length > 0 && (
                <div className="pt-6 border-t border-slate-100 mt-6">
                    <TablePagination
                        paginationData={paginationData}
                        setPaginationData={setPaginationData}
                        handleChangePageSize={handleChangePageSize}
                        isLoading={isLoading}
                    />
                </div>
            )}
        </Layout>
    )
}

export default ExitClearanceList
